import axios from 'axios';
import API from "../config/API";
import siteSetting from "../config/env";

export const marketplaceService = {
    getMarketplaceHome,
    getRecommendedSalons,
    getNewSalons,
    searchSalons,
    getCategories,
    getCurrentLocation,
    getCachedLocation,
    clearCachedLocation,
    persistLocation,
    getSalonSummary,
    getStaffSummary,
    getDateRangeAvailability,
    getAvailableSlots,
    // Client availability (new API — supports "any staff" mode)
    getClientAvailabilitySlots,
    getClientAvailabilityDateRange,
    // Discover more
    getDiscoverMoreSalons,
    getDiscoverMoreStylists,
    // Favorites
    toggleFavoriteSalon,
    toggleFavoriteStylist,
    getFavoriteSalons,
    getFavoriteStylists,
    checkFavoriteStatus,
    getAllFavorites,
    // Browse / landing pages
    getBrowseLocations,
    getBrowsePage
};

// Default London coordinates — used when we have no cached location and
// geolocation hasn't resolved yet.
const DEFAULT_LOCATION = {
    latitude: 51.5074,
    longitude: -0.1278
};

// localStorage key + TTL for caching the last known accurate location so the
// marketplace can render immediately on cold start without waiting on the
// native geolocation permission prompt.
const LOCATION_CACHE_KEY = 'beu.lastKnownLocation.v1';
const LOCATION_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Synchronous read of the best location we can produce without waiting on
 * geolocation. Returns the cached location if it's fresh (<24h), otherwise
 * the default. Never returns null — callers can always pass the result
 * straight into an API call.
 */
function getCachedLocation() {
    try {
        const raw = localStorage.getItem(LOCATION_CACHE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (
                parsed &&
                typeof parsed.latitude === 'number' &&
                typeof parsed.longitude === 'number' &&
                typeof parsed.timestamp === 'number' &&
                Date.now() - parsed.timestamp < LOCATION_CACHE_TTL_MS
            ) {
                return { latitude: parsed.latitude, longitude: parsed.longitude };
            }
        }
    } catch (err) {
        // localStorage can throw in private mode / very old WebViews — fall through.
    }
    return DEFAULT_LOCATION;
}

function persistLocation(location) {
    try {
        localStorage.setItem(
            LOCATION_CACHE_KEY,
            JSON.stringify({
                latitude: location.latitude,
                longitude: location.longitude,
                timestamp: Date.now()
            })
        );
    } catch (err) {
        // Silent — we'll just miss the cache on next boot.
    }
}

/**
 * Wipe the cached last-known location so the next cold start falls back to
 * the London default. Use this if we've detected that the cached location
 * returns no usable data (e.g. user is outside coverage) — otherwise every
 * subsequent cold start would repeat the same empty response.
 */
function clearCachedLocation() {
    try {
        localStorage.removeItem(LOCATION_CACHE_KEY);
    } catch (err) {
        // Silent — nothing we can do.
    }
}

/**
 * Get user's current location using browser geolocation API
 * Falls back to default London coordinates if geolocation fails or times out
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
function getCurrentLocation() {
    // Default London coordinates
    const defaultLocation = DEFAULT_LOCATION;

    return new Promise((resolve) => {
        // Check if geolocation is available
        if (!navigator.geolocation) {
            console.warn('[Geolocation] Not supported by browser, using default location');
            resolve(defaultLocation);
            return;
        }

        let resolved = false;
        
        // Create a manual timeout that will definitely fire
        // This is important for Capacitor where the native timeout may not work properly
        const timeoutId = setTimeout(() => {
            if (!resolved) {
                resolved = true;
                console.warn('[Geolocation] Timeout after 5 seconds, using default location');
                resolve(defaultLocation);
            }
        }, 5000); // 5 second timeout for better UX in mobile apps

        try {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        console.log('[Geolocation] Success:', position.coords.latitude, position.coords.longitude);
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        });
                        // Note: we deliberately don't persist here. The caller
                        // (NewHomePage) persists only AFTER confirming the
                        // coords returned a non-empty payload - otherwise an
                        // out-of-coverage user's coords get cached and poison
                        // every subsequent cold start.
                    }
                },
                (error) => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        // Default to London coordinates if geolocation fails
                        console.warn('[Geolocation] Error:', error.code, error.message, '- using default location');
                        resolve(defaultLocation);
                    }
                },
                {
                    enableHighAccuracy: false, // Set to false for faster response on mobile
                    timeout: 5000,
                    maximumAge: 300000 // Cache for 5 minutes
                }
            );
        } catch (err) {
            if (!resolved) {
                resolved = true;
                clearTimeout(timeoutId);
                console.warn('[Geolocation] Exception:', err.message, '- using default location');
                resolve(defaultLocation);
            }
        }
    });
}

/**
 * Get marketplace homepage data (recommended, new salons, categories, and favorites if logged in)
 * @param {Object} params - Query parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 */
function getMarketplaceHome(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    // Add auth token if user is logged in (to get favorites)
    const accessToken = localStorage.getItem('accessToken');
    console.log('[Marketplace] getMarketplaceHome - accessToken present:', !!accessToken);
    if (accessToken) {
        headers['accessToken'] = accessToken;
        console.log('[Marketplace] Adding accessToken to headers');
    }
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance })
    };

    console.log('[Marketplace] Request headers:', headers);
    return axios.get(API.MARKETPLACE_HOME, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get recommended salons
 * @param {Object} params - Query parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 * @param {number} [params.limit] - Number of results
 * @param {number} [params.skip] - Pagination offset
 */
function getRecommendedSalons(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance }),
        ...(params.limit && { limit: params.limit }),
        ...(params.skip && { skip: params.skip })
    };

    return axios.get(API.MARKETPLACE_RECOMMENDED, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get new salons (New to Beu)
 * @param {Object} params - Query parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 * @param {number} [params.daysAgo] - Days to consider as "new"
 * @param {number} [params.limit] - Number of results
 * @param {number} [params.skip] - Pagination offset
 */
function getNewSalons(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance }),
        ...(params.daysAgo && { daysAgo: params.daysAgo }),
        ...(params.limit && { limit: params.limit }),
        ...(params.skip && { skip: params.skip })
    };

    return axios.get(API.MARKETPLACE_NEW, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Search salons with filters
 * @param {Object} params - Search parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 * @param {string} [params.searchQuery] - Text search query
 * @param {string} [params.category] - Category filter
 * @param {number} [params.minPrice] - Minimum price filter
 * @param {number} [params.maxPrice] - Maximum price filter
 * @param {string} [params.date] - Date filter (YYYY-MM-DD)
 * @param {string} [params.sortBy] - Sort field
 * @param {string} [params.sortOrder] - Sort order (asc/desc)
 * @param {number} [params.limit] - Number of results
 * @param {number} [params.skip] - Pagination offset
 */
function searchSalons(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance }),
        ...(params.searchQuery && { searchQuery: params.searchQuery }),
        ...(params.category && { category: params.category }),
        ...(params.minPrice !== undefined && { minPrice: params.minPrice }),
        ...(params.maxPrice !== undefined && { maxPrice: params.maxPrice }),
        ...(params.date && { date: params.date }),
        ...(params.sortBy && { sortBy: params.sortBy }),
        ...(params.sortOrder && { sortOrder: params.sortOrder }),
        ...(params.limit && { limit: params.limit }),
        ...(params.skip && { skip: params.skip })
    };

    // axios 0.19 predates AbortSignal support, so cancellation goes through
    // a CancelToken. Callers pass params.cancelToken to have a superseded
    // search aborted rather than raced (see searchSalons.cancelSource).
    return axios.get(API.MARKETPLACE_SEARCH, {
        params: queryParams,
        headers,
        ...(params.cancelToken && { cancelToken: params.cancelToken })
    })
        .then(handleResponse)
        .catch(handleError);
}

/** Mint a cancel source for searchSalons: pass .token in, call .cancel() to abort. */
searchSalons.cancelSource = () => axios.CancelToken.source();

/** True when a rejection is just a superseded/cancelled request. */
searchSalons.isCancelled = (err) => axios.isCancel(err);

/**
 * Get all available categories
 */
function getCategories() {
    const headers = {
        'Content-Type': 'application/json'
    };

    return axios.get(API.MARKETPLACE_CATEGORIES, { headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get discover more salons (paginated, excluding already seen salons)
 * @param {Object} params - Query parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 * @param {number} [params.limit] - Number of results (default 4)
 * @param {number} [params.skip] - Pagination offset
 * @param {string[]} [params.excludeIds] - Array of salon IDs to exclude
 */
function getDiscoverMoreSalons(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance }),
        limit: params.limit || 4,
        ...(params.skip && { skip: params.skip }),
        ...(params.excludeIds && params.excludeIds.length > 0 && { excludeIds: params.excludeIds.join(',') })
    };

    return axios.get(API.MARKETPLACE_RECOMMENDED, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get discover more stylists (paginated, excluding already seen stylists)
 * @param {Object} params - Query parameters
 * @param {number} params.latitude - User's latitude
 * @param {number} params.longitude - User's longitude
 * @param {number} [params.maxDistance] - Maximum distance in meters
 * @param {number} [params.limit] - Number of results (default 4)
 * @param {number} [params.skip] - Pagination offset
 * @param {string[]} [params.excludeIds] - Array of stylist IDs to exclude
 */
function getDiscoverMoreStylists(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        latitude: params.latitude,
        longitude: params.longitude,
        ...(params.maxDistance && { maxDistance: params.maxDistance }),
        limit: params.limit || 4,
        ...(params.skip && { skip: params.skip }),
        ...(params.excludeIds && params.excludeIds.length > 0 && { excludeIds: params.excludeIds.join(',') })
    };

    // Use the recommended endpoint which returns stylists
    return axios.get(API.MARKETPLACE_RECOMMENDED, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get comprehensive salon summary for salon detail page
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {number} [params.latitude] - User's latitude (for distance calculation)
 * @param {number} [params.longitude] - User's longitude (for distance calculation)
 */
function getSalonSummary(params) {
    const headers = {
        'Content-Type': 'application/json'
    };
    
    const queryParams = {
        ...(params.latitude && { latitude: params.latitude }),
        ...(params.longitude && { longitude: params.longitude })
    };

    return axios.get(`${API.SALON_SUMMARY}/${params.salonId}/summary`, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get comprehensive staff member summary for staff detail page
 * @param {Object} params - Query parameters
 * @param {string} params.hairdresserId - Staff member user ID
 * @param {number} [params.latitude] - User's latitude (for distance calculation)
 * @param {number} [params.longitude] - User's longitude (for distance calculation)
 */
function getStaffSummary(params) {
    const headers = {
        'Content-Type': 'application/json'
    };

    const queryParams = {
        ...(params.latitude && { latitude: params.latitude }),
        ...(params.longitude && { longitude: params.longitude }),
        ...(params.salonId && { salonId: params.salonId })
    };

    return axios.get(`${API.STAFF_SUMMARY}/${params.hairdresserId}/summary`, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get date range availability for a hairdresser
 * Used for the date scroller in the booking flow
 * @param {Object} params - Request parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.hairdresserId - Hairdresser ID
 * @param {string} params.startDate - Start date in YYYY-MM-DD format
 * @param {string} params.endDate - End date in YYYY-MM-DD format
 * @param {number} [params.serviceTime] - Service time in minutes
 */
function getDateRangeAvailability(params) {
    const headers = {
        'Content-Type': 'application/json'
    };

    const url = `${siteSetting.api.v2}salon/${params.salonId}/hairdresser/${params.hairdresserId}/date-range-availability`;

    const body = {
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.serviceTime && { serviceTime: params.serviceTime })
    };

    return axios.post(url, body, { headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get available time slots for a specific date
 * Used for showing available booking times
 * @param {Object} params - Query parameters
 * @param {string} params.salonId - Salon ID
 * @param {string} params.hairdresserId - Hairdresser ID
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {number} params.serviceTime - Total service duration in minutes
 */
function getAvailableSlots(params) {
    const headers = {
        'Content-Type': 'application/json'
    };

    const queryParams = {
        date: params.date,
        serviceTime: params.serviceTime
    };

    const url = `${siteSetting.api.v2}salon/${params.salonId}/hairdresser/${params.hairdresserId}/available-slots`;

    return axios.get(url, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get available slots via the new client-availability API.
 * Supports "any staff" mode — omit staffId to get merged availability across all staff.
 * @param {Object} params
 * @param {string} params.salonId
 * @param {string} [params.staffId] - optional, omit for "any staff"
 * @param {string} params.date - YYYY-MM-DD
 * @param {number} params.serviceTime - total service duration in minutes
 */
function getClientAvailabilitySlots(params) {
    const headers = { 'Content-Type': 'application/json' };

    const queryParams = {
        date: params.date,
        ...(params.serviceTime && { serviceTime: params.serviceTime }),
        ...(params.staffId && { staffId: params.staffId }),
        ...(Array.isArray(params.serviceIds) && params.serviceIds.length > 0 && {
            serviceIds: params.serviceIds.join(',')
        })
    };

    const url = `${siteSetting.api.v2}client-availability/salon/${params.salonId}/slots`;

    return axios.get(url, { params: queryParams, headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Check date-range availability via the new client-availability API.
 * Supports "any staff" mode — omit staffId to check across all staff.
 * @param {Object} params
 * @param {string} params.salonId
 * @param {string} [params.staffId] - optional, omit for "any staff"
 * @param {string} params.startDate - YYYY-MM-DD
 * @param {string} params.endDate - YYYY-MM-DD
 * @param {number} [params.serviceTime]
 */
function getClientAvailabilityDateRange(params) {
    const headers = { 'Content-Type': 'application/json' };

    const url = `${siteSetting.api.v2}client-availability/salon/${params.salonId}/date-range`;

    const body = {
        startDate: params.startDate,
        endDate: params.endDate,
        ...(params.serviceTime && { serviceTime: params.serviceTime }),
        ...(params.staffId && { staffId: params.staffId }),
        ...(Array.isArray(params.serviceIds) && params.serviceIds.length > 0 && {
            serviceIds: params.serviceIds
        })
    };

    return axios.post(url, body, { headers })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Handle successful API response
 * @param {Object} response - Axios response object
 */
function handleResponse(response) {
    if (response.data && response.data.statusCode === 1) {
        return response.data.responseData;
    }
    throw new Error(response.data?.message || 'An error occurred');
}

/**
 * Handle API error
 * @param {Error} error - Error object
 */
function handleError(error) {
    // A superseded request (the user typed another character) is expected,
    // not a failure - rethrow quietly so callers can ignore it and it never
    // reaches the console as an error.
    if (axios.isCancel(error)) {
        throw error;
    }
    console.error('Marketplace API Error:', error);
    if (error.response) {
        throw new Error(error.response.data?.message || 'Server error');
    } else if (error.request) {
        throw new Error('Network error - please check your connection');
    } else {
        throw error;
    }
}

// ========================== Favorites API ==========================

/**
 * Get auth headers for authenticated requests
 */
function getAuthHeaders() {
    return {
        'Content-Type': 'application/json',
        'accessToken': localStorage.getItem('accessToken')
    };
}

/**
 * Toggle favorite status for a salon
 * @param {Object} params - Parameters
 * @param {string} params.salonId - Salon ID
 * @param {boolean} params.isFavorite - Whether to add or remove
 * @returns {Promise<Object>} Result
 */
function toggleFavoriteSalon(params) {
    return axios.post(`${siteSetting.api.v2}favorites/salon`, {
        salonId: params.salonId,
        isFavorite: params.isFavorite
    }, { headers: getAuthHeaders() })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Toggle favorite status for a stylist
 * @param {Object} params - Parameters
 * @param {string} params.stylistId - Hairdresser ID
 * @param {boolean} params.isFavorite - Whether to add or remove
 * @returns {Promise<Object>} Result
 */
function toggleFavoriteStylist(params) {
    return axios.post(`${siteSetting.api.v2}favorites/stylist`, {
        stylistId: params.stylistId,
        isFavorite: params.isFavorite
    }, { headers: getAuthHeaders() })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get user's favorite salons
 * @returns {Promise<Object>} Salons list
 */
function getFavoriteSalons() {
    return axios.get(`${siteSetting.api.v2}favorites/salons`, {
        headers: getAuthHeaders()
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get user's favorite stylists
 * @returns {Promise<Object>} Stylists list
 */
function getFavoriteStylists() {
    return axios.get(`${siteSetting.api.v2}favorites/stylists`, {
        headers: getAuthHeaders()
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Check favorite status for salon and/or stylist
 * @param {Object} params - Parameters
 * @param {string} [params.salonId] - Salon ID to check
 * @param {string} [params.stylistId] - Stylist ID to check
 * @returns {Promise<Object>} Status
 */
function checkFavoriteStatus(params) {
    const queryParams = {};
    if (params.salonId) queryParams.salonId = params.salonId;
    if (params.stylistId) queryParams.stylistId = params.stylistId;
    
    return axios.get(`${siteSetting.api.v2}favorites/check`, {
        params: queryParams,
        headers: getAuthHeaders()
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get all favorite IDs for the user
 * @returns {Promise<Object>} All favorite IDs
 */
function getAllFavorites() {
    return axios.get(`${siteSetting.api.v2}favorites/all`, {
        headers: getAuthHeaders()
    })
        .then(handleResponse)
        .catch(handleError);
}

// ========================== Browse / Landing Pages ==========================

/**
 * Get all locations with salon counts per business type.
 * Used for internal linking and navigation.
 */
function getBrowseLocations() {
    return axios.get(API.BROWSE_LOCATIONS)
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get salons for a category + location landing page.
 * @param {string} category - Category slug (e.g. "barbers")
 * @param {string} location - Location slug (e.g. "fulham")
 */
function getBrowsePage(category, location) {
    return axios.get(`${API.BROWSE_CATEGORY}/${category}/in/${location}`)
        .then(handleResponse)
        .catch(handleError);
}